---
title: Factory Support and Agent Plugins
description: Factory integration, external agent plugins that work without forking the CLI, faster Git hooks, and pinned repositories on entire.io.
category: Changelog
author: Marvin
---

Beep, boop. It's time for Dispatch 0x0004.

In a world with many agents, we believe your context — from chat sessions to logs and prompts — should automatically sync to the Git repository. With the Entire CLI, we are building a portable agentic context layer you can share with colleagues, collaborators, and friends.

With your help, this week we pushed a new Factory integration 🎉, created a new Repositories section on [entire.io](https://entire.io) that lets you see all your org's repos, introduced a new external agent plugin system to help you bring your favorite agents to the Entire CLI, and lots of smaller improvements 👇

## Agent Plugin System

- We're making it easier to integrate third party agents without forking the CLI. Agents can now run as standalone programs that follow a [simple protocol](https://docs.entire.io/cli/external-agents), allowing them to be discovered on your system and used alongside built-in agents.

## [entire.io](https://entire.io) User Interface Updates

- See recently used repos in the sidebar and pin your most important ones for quick access.
- The checkpoints list now has more compact single-line rows for easier scanning grouped by day.
- A new breadcrumb header now lets you browse between commits within a Checkpoint.

## Faster Hook Performance

We've heard your feedback. This week we made several ships to introduce various performance improvements to the post-commit behaviour, especially in larger repos.

- Replaced slow in-memory tree comparisons with native git operations, reducing hook latency on large repos.
- Added structured performance instrumentation. The new span-based perf package tracks timing across lifecycle handlers with nested breakdowns, making performance issues easier to investigate.
- To eliminate redundant calls in the prepare-commit-msg hook, staged file lists are now cached across sessions, and go-git's `worktree.Status()` (which scans all files) was replaced with native `git diff --cached`.
- Replaced O(N) go-git tree walks with git diff-tree in post-commit hook for faster commits.

## Further Improvements

### Updates to Cursor, Factory & OpenCode Agents

- **Cursor** now has full transcript analysis, enabling prompt extraction for shadow branch commit messages, summary extraction, and transcript position tracking.
  - Session metrics, including duration, turns, model name, and attribution, are now captured via hooks.
    - Thank you to [@MarkoTatic](https://github.com/MarkoTatic) for the detailed Cursor bug reports that helped drive these improvements!
- **OpenCode** sessions now end cleanly on quit via `server.instance.disposed` instead of lingering as active.
  - Thank you to [@petrenk0n](https://github.com/petrenk0n) for reporting the empty sessions issue!
- An E2E test runner was added for **Factory** sessions with full interactive flow coverage.
  - Thank you to [@SvenMeyer](https://github.com/SvenMeyer) for requesting Droid support!

### Squash Merge Resume & Rewind

- `entire resume` parses all trailers, deduplicates sessions by ID, and sorts by timestamp to restore the correct state.
- `entire resume` on squash commits shows only the latest checkpoint instead of a confusing list of all sessions across the branch.
- `entire rewind` is fixed to pick the _latest_ checkpoint in squash-merged commits, not just the first regex match.
- `entire rewind` is now able to handle squash commits properly.

### Model Tracking in Checkpoints

- Checkpoints now capture which LLM model was used, persisted to `metadata.json` and visible in session info. The model is resolved from agent-specific hook payloads (Claude Code's `SessionStart`, Gemini CLI's `BeforeModel`).

### Session & Hook Reliability

- Stale sessions are now properly cleaned up instead of lingering indefinitely.
- Fixed a bug where hooks could silently skip files that should have been tracked across turns.
- `entire explain` no longer hangs on repos with many checkpoints.
- Corrected inconsistent terminal behavior across different hook paths.
  - Thank you to [@dismory](https://github.com/dismory) for reporting the worktree config issue!

### Setup & Developer Experience

- `entire enable` now supports `--absolute-git-hook-path` to set up git hooks with absolute paths to the entire binary, useful for environments where PATH resolution varies between shells or CI runners.
- Commit signing no longer breaks the test suite for contributors who have it enabled globally.
  - Thank you to [@erezrokah](https://github.com/erezrokah) for catching this and contributing the fix!

Thanks for all your help this week, rebels. Next week we bring the Entire CLI to even more agents, so you can drill into your context and session history with a universal reasoning layer, no matter which AI companion you build with.

Boop.
